// One chart for comparing games through the day: occupancy, traffic or queues,
// lined up by game start (default) or by clock time.
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart as ChartJS } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  ChartHeader,
  ChartSegmented,
  applyChartTheme,
  sectionLineDataset,
  hoverGuidePlugin,
  CHART_THEME,
} from '../../Concession/charts/chartTheme';
import { minuteToClock } from './overviewMetrics';

ChartJS.register(annotationPlugin);

const offsetLabel = (v, long = false) => {
  if (v === 0) return long ? 'Game start' : 'Start';
  const sign = v > 0 ? '+' : '−';
  const h = Math.floor(Math.abs(v) / 60);
  const m = Math.abs(v) % 60;
  if (!long) return m === 0 ? `${sign}${h}h` : '';
  return `${sign}${h ? `${h}h ` : ''}${m ? `${m}m` : ''}`.trim() + ' from start';
};

export default function OverviewCompareChart({
  metricOptions,
  metric,
  onMetric,
  align,
  onAlign,
  series,
  unit,
  yTitle,
  subtitle,
  startMarkers,
  title = 'Through the day',
  resolution,
  onResolution,
  // Optional extra switch shown after the metric selector (e.g. Running total / Per interval)
  extraControls = null,
}) {
  const chartData = useMemo(() => ({
    datasets: series.map((s) => ({
      label: s.label,
      data: s.points,
      ...sectionLineDataset(s.color),
      fill: false,
      borderWidth: 2.25,
    })),
  }), [series]);

  const options = useMemo(() => {
    const base = applyChartTheme({
      responsive: true,
      maintainAspectRatio: false,
      parsing: false,
      scales: {
        x: {
          type: 'linear',
          ticks: {
            stepSize: 60,
            callback: (v) => (align === 'start' ? offsetLabel(v) : (v % 60 === 0 ? minuteToClock(v).replace(':00', '') : '')),
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: yTitle },
          ticks: { precision: 0 },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const x = items[0]?.parsed?.x;
              if (x == null) return '';
              return align === 'start' ? offsetLabel(x, true) : minuteToClock(x);
            },
            label: (item) => ` ${item.dataset.label}: ${item.parsed.y} ${unit}`,
          },
        },
        annotation: {
          annotations: align === 'start'
            ? [{
                type: 'line',
                scaleID: 'x',
                value: 0,
                borderColor: CHART_THEME.gameStart,
                borderWidth: 1.5,
                borderDash: [6, 4],
                label: {
                  display: true,
                  content: 'Game start',
                  position: 'start',
                  backgroundColor: 'rgba(255, 255, 255, 0.92)',
                  color: CHART_THEME.gameStart,
                  font: { family: CHART_THEME.font, size: 11, weight: '600' },
                  padding: { x: 6, y: 2 },
                },
              }]
            : (startMarkers || []).map((mk) => ({
                type: 'line',
                scaleID: 'x',
                value: mk.minute,
                borderColor: mk.color,
                borderWidth: 1.25,
                borderDash: [4, 4],
              })),
        },
      },
    });
    // Games are bucketed onto the same x grid, so compare every game at the hovered time.
    // 'nearest' along x picks the one closest time (one value per game); 'x' mode listed every
    // point within the hit radius, which at 1-minute intervals meant two or more minutes per game.
    base.interaction = { mode: 'nearest', axis: 'x', intersect: false };
    return base;
  }, [align, unit, yTitle, startMarkers]);

  const hasData = series.some((s) => s.points.length > 0);

  return (
    <div className="graph panel-2 cx-chart-card rx-ov-chart">
      <ChartHeader
        title={title}
        subtitle={subtitle}
        controls={(
          <>
            {metricOptions.length > 1 && (
              <ChartSegmented options={metricOptions} value={metric} onChange={onMetric} />
            )}
            {extraControls}
            {onResolution && (
              <ChartSegmented
                label="Every"
                options={[
                  { value: 1, label: '1m' },
                  { value: 5, label: '5m' },
                  { value: 15, label: '15m' },
                  { value: 30, label: '30m' },
                  { value: 60, label: '1h' },
                ]}
                value={resolution}
                onChange={onResolution}
              />
            )}
            <ChartSegmented
              label="Line up by"
              options={[{ value: 'start', label: 'Game start' }, { value: 'clock', label: 'Clock time' }]}
              value={align}
              onChange={onAlign}
            />
          </>
        )}
      />
      <div className="rx-ov-legend">
        {series.map((s) => (
          <span key={s.key} className="rx-ov-legend-item">
            <span className="cx-ov-swatch" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
      <div style={{ position: 'relative', height: 340 }}>
        {hasData ? (
          <Line data={chartData} options={options} plugins={[hoverGuidePlugin]} />
        ) : (
          <div className="cx-empty">No data for the selected games</div>
        )}
      </div>
    </div>
  );
}
